// The Google REST transport. Every request carries the caller's OAuth access
// token as a bearer token; the interface decides where that token comes from
// (the web's Google store, the MCP's google_token lookup).

/** A token, or a function that resolves one per request. After Google answers
 *  401 it is called again with `refresh: true`, so a source that caches can get
 *  a fresh token before the one retry. */
export type TokenSource =
  | string
  | ((opts?: {
      refresh?: boolean
    }) => string | null | undefined | Promise<string | null | undefined>)

export interface GoogleOptions {
  token: TokenSource
}

/** A non-2xx response. `messages` are Google's own, one per entry of its
 *  `error.errors` (or its top-level `error.message` when it sends no list). */
export class GoogleError extends Error {
  status: number
  messages: string[]
  constructor(message: string, status: number, messages: string[] = []) {
    super(message)
    this.name = "GoogleError"
    this.status = status
    this.messages = messages
  }
}

/** The token source has no token: Google is not connected for this user. */
export class GoogleNotConnected extends GoogleError {
  constructor() {
    super("No Google token", 401)
    this.name = "GoogleNotConnected"
  }
}

export type Params = Record<string, string | number | boolean | string[] | undefined>

export interface GoogleApi {
  get<T>(url: string, params?: Params): Promise<T>
  patch<T>(url: string, body: unknown, params?: Params): Promise<T>
  post<T>(url: string, body?: unknown, params?: Params): Promise<T>
}

interface ErrorBody {
  error?: { message?: string; errors?: Array<{ message?: string }> }
}

function withParams(url: string, params: Params): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue
    for (const item of Array.isArray(v) ? v : [v]) qs.append(k, String(item))
  }
  const query = qs.toString()
  return query ? `${url}?${query}` : url
}

export function createApi({ token }: GoogleOptions): GoogleApi {
  const resolveToken = async (opts?: { refresh?: boolean }): Promise<string | null> => {
    const value = typeof token === "function" ? await token(opts) : token
    return value || null
  }

  const send = (method: string, url: string, params: Params, bearer: string, body?: unknown) =>
    fetch(withParams(url, params), {
      method,
      headers: {
        authorization: `Bearer ${bearer}`,
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })

  const request = async <T>(method: string, url: string, params: Params, body?: unknown) => {
    const bearer = await resolveToken()
    if (!bearer) throw new GoogleNotConnected()
    let res = await send(method, url, params, bearer, body)
    if (res.status === 401 && typeof token === "function") {
      const fresh = await resolveToken({ refresh: true })
      if (fresh && fresh !== bearer) res = await send(method, url, params, fresh, body)
    }
    if (!res.ok) {
      const json = (await res.json().catch(() => ({}))) as ErrorBody
      const listed = (json.error?.errors ?? []).flatMap((e) => (e.message ? [e.message] : []))
      const messages = listed.length ? listed : json.error?.message ? [json.error.message] : []
      const detail = json.error?.message || res.statusText
      throw new GoogleError(
        `Google ${method} ${new URL(url).pathname} failed (${res.status})${detail ? `: ${detail}` : ""}`,
        res.status,
        messages
      )
    }
    return (res.status === 204 ? undefined : await res.json()) as T
  }

  return {
    get: (url, params = {}) => request("GET", url, params),
    patch: (url, body, params = {}) => request("PATCH", url, params, body),
    post: (url, body, params = {}) => request("POST", url, params, body),
  }
}
